import { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import NumberFormat from "react-number-format"
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis } from "recharts"
import { Breadcrumb } from "../Breadcrumb"
import { Model } from "../../../models/model"
import "../EstadoResultado/estadoResultado.css"

const customerReport = {
    route: 'pc',
    endpoint: 'customers',
    title: 'Ventas por cliente',
    question: '¿Quiénes generan más ventas, cuánto dependemos de ellos y qué margen dejan?',
    count: 'Clientes con ventas',
    chart: 'Clientes con más ventas',
    search: 'Buscar cliente',
    column: 'Cliente',
    empty: 'No hay clientes que coincidan con la búsqueda.',
}

export const VentasPorClientes = () => <VentasAgrupadas config={customerReport} />

export const VentasAgrupadas = ({ config }) => {
    const { local_simbolo, local_separacion_miles, local_separacion_decimales } = useSelector(state => state.general.info)
    const currentYear = new Date().getFullYear()
    const [year, setYear] = useState(currentYear)
    const [sort, setSort] = useState('total_venta')
    const [search, setSearch] = useState('')
    const [refresh, setRefresh] = useState(0)
    const [report, setReport] = useState(null)

    useEffect(() => {
        let active = true
        const load = async () => {
            const date_report = new Date(year, 0, 1, 12).toISOString()
            const query = new URLSearchParams({ date_report })
            const result = await Model.getAll({ url: `reports/${config.endpoint}/sales?${query}` })
            if (active) setReport({ year, refresh, result })
        }
        load()
        return () => { active = false }
    }, [year, refresh, config.endpoint])

    const loaded = report?.year === year && report?.refresh === refresh
    const result = loaded ? report.result : null
    const rows = (result?.data || []).map(row => ({ ...row, label: row.cliente ?? row.area }))
    const groups = rows.filter(row => row.id != null && Number(row.total_venta) > 0)
    const top = groups.slice(0, 5).map(row => ({
        ...row, total_venta: Number(row.total_venta),
    }))
    const visible = rows.filter(row => row.label.toLocaleLowerCase('es').includes(search.trim().toLocaleLowerCase('es')))
    if (sort === 'margen') {
        visible.sort((a, b) => (b.margen == null ? -Infinity : Number(b.margen)) - (a.margen == null ? -Infinity : Number(a.margen)))
    }

    const amount = (value, percent = false) => value == null ? '—' : (
        <NumberFormat displayType="text" value={value}
            thousandSeparator={local_separacion_miles} decimalSeparator={local_separacion_decimales}
            prefix={percent ? '' : `${local_simbolo || 'S/'} `} suffix={percent ? ' %' : ''}
            decimalScale={2} fixedDecimalScale />
    )

    return (
        <div className="p-container er-report">
            <div className="mt-3">
                <Breadcrumb routes={[
                    { id: 1, name: 'Reportes', link: '/reportes', active: false },
                    { id: 2, name: config.title, link: `/reportes/ventas/${config.route}`, active: true },
                ]} />
            </div>
            <header className="er-heading">
                <div>
                    <span className="er-eyebrow">Reportes de ventas</span>
                    <h1 className="p-title">{config.title}</h1>
                    <p>{config.question}</p>
                </div>
                <div className="er-filters d-flex flex-wrap gap-2 align-items-end">
                    <div>
                        <label className="form-label" htmlFor="customer-sales-year">Año</label>
                        <input className="form-control" id="customer-sales-year" type="number" min="1900" max="9999"
                            defaultValue={currentYear} onChange={event => {
                                if (event.target.validity.valid && event.target.value) setYear(Number(event.target.value))
                            }} />
                    </div>
                    <button type="button" className="btn p-btn-secondary" disabled={!loaded} onClick={() => setRefresh(value => value + 1)}>
                        Actualizar
                    </button>
                </div>
            </header>
            {!loaded && <p role="status">Cargando reporte de ventas…</p>}
            {loaded && !result && <p role="alert">No se pudo cargar el reporte. Intenta actualizarlo.</p>}
            {result && !rows.length && <p role="status">No hay proyectos aprobados para {year}.</p>}
            {rows.length > 0 && (
                <>
                    <p className="er-source mb-3">Proyectos aprobados asignados en {year}. Importes en moneda local.</p>
                    <div className="row g-2 mb-3">
                        {[
                            ['Ventas netas', amount(result.total)],
                            [config.count, groups.length],
                            ['Proyectos aprobados', rows.reduce((sum, row) => sum + Number(row.cantidad), 0)],
                            ['Participación de los 3 principales', amount(result.total > 0 ? top.slice(0, 3).reduce((sum, row) => sum + row.percent, 0) : null, true)],
                        ].map(([label, value]) => (
                            <div className="col-6 col-lg-3" key={label}>
                                <div className="er-chart h-100 mb-0">
                                    <div className="er-source">{label}</div>
                                    <strong className="fs-5">{value}</strong>
                                </div>
                            </div>
                        ))}
                    </div>
                    {top.length > 0 && (
                        <figure className="er-chart">
                            <figcaption className="fw-bold mb-2">{config.chart}</figcaption>
                            <ResponsiveContainer width="100%" height={top.length * 36 + 12}>
                                <BarChart layout="vertical" data={top}>
                                    <XAxis type="number" hide />
                                    <YAxis type="category" dataKey="label" width={120} tick={{ fontSize: 11 }}
                                        tickFormatter={name => name.toLocaleUpperCase('es')} />
                                    <Bar dataKey="total_venta" fill="var(--primary-color)" maxBarSize={22} isAnimationActive={false} />
                                </BarChart>
                            </ResponsiveContainer>
                            <ul className="list-unstyled mb-0">
                                {top.map(row => <li key={row.id}><small className="text-uppercase">{row.label}</small> · {amount(row.total_venta)} · {amount(row.percent, true)}</li>)}
                            </ul>
                        </figure>
                    )}
                    <div className="d-flex flex-wrap gap-2 mb-2">
                        <div>
                            <label className="form-label" htmlFor="customer-sales-search">{config.search}</label>
                            <input className="form-control" id="customer-sales-search" type="search"
                                value={search} onChange={event => setSearch(event.target.value)} />
                        </div>
                        <div>
                            <label className="form-label" htmlFor="customer-sales-sort">Ordenar por</label>
                            <select className="form-select" id="customer-sales-sort" value={sort} onChange={event => setSort(event.target.value)}>
                                <option value="total_venta">Ventas netas</option>
                                <option value="margen">Margen directo</option>
                            </select>
                        </div>
                    </div>
                    <p className="er-source mb-2">El margen directo descuenta los costos directos registrados de los proyectos.</p>
                    <div className="table-responsive er-table-wrap">
                        <table className="table er-table mb-0">
                            <thead><tr>
                                <th scope="col">{config.column}</th>
                                <th scope="col" className="text-end">Ventas netas</th>
                                <th scope="col" className="text-end">% de ventas</th>
                                <th scope="col" className="text-end">Proyectos</th>
                                <th scope="col" className="text-end">Margen directo</th>
                            </tr></thead>
                            <tbody>
                                {visible.map(row => (
                                    <tr key={row.id ?? 'unassigned'}>
                                        <th scope="row"><small className="text-uppercase fw-normal">{row.label}</small></th>
                                        <td className="text-end text-nowrap">{amount(row.total_venta)}</td>
                                        <td className="text-end">{amount(row.percent, true)}</td>
                                        <td className="text-end">{row.cantidad}</td>
                                        <td className={`text-end ${Number(row.margen) < 0 ? 'text-danger' : ''}`}>{amount(row.margen, true)}</td>
                                    </tr>
                                ))}
                                {!visible.length && <tr><td colSpan={5}>{config.empty}</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    )
}
